import { DownloadIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { Box, Flex, IconButton, Tag, Text, Tooltip } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import React from "react";
import { z } from "zod";
import { BodyOf, ResponseOf } from "../../core/api";
import DataTable from "../../shared/components/DataTable/DataTable";
import useEndpointDataTable from "../../shared/components/DataTable/useEndpointDataTable";
import SingleDatePicker from "../../shared/components/DatePicker/SingleDatePicker";
import Page from "../../shared/components/Page";
import Select from "../../shared/components/Select";
import useApi from "../../shared/hooks/useApi";
import usePathParams from "../../shared/hooks/usePathParams";
import { OfficeId, PatientId } from "../../shared/schema/schema";
import { dateFormatter } from "../../shared/utils/date-formatter";
import DailyNoteCosignModal from "./components/DailyNoteCosignModal";
import { useAttestationPdfDownload } from "./components/useAttestationPdfDownload";
import { usePatientSearchFilter } from "./components/usePatientSearchFilter";
import { ResultCount } from "./dailyNotesShared";

type PageBody = BodyOf<"post", "./visit_daily_note_attestations/page">;
export type AttestationListItem = ResponseOf<
  "post",
  "./visit_daily_note_attestations/page"
>["items"][number];

type Filters = {
  status: PageBody["status"];
  officeId: PageBody["officeId"];
  patientId: PageBody["patientId"];
  fromDate: PageBody["fromDate"];
  toDate: PageBody["toDate"];
};

// Four, not two. "Completed" used to cover both a batch that was counter-signed and
// one that never needed to be — the same state to a queue, different states to anyone
// reading the record back.
const STATUS_OPTIONS = [
  { label: "All", value: "ALL" },
  { label: "Awaiting 2nd reviewer", value: "AWAITING_COSIGNATURE" },
  { label: "Signed", value: "SIGNED" },
  { label: "Co-signed", value: "COSIGNED" },
];

/** Matches the notes tab, so the two tabs open on the same window. */
const DEFAULT_MONTHS_BACK = 3;

/** The filters a Dashboard tile opens this tab with. */
const routeParams = z.object({
  attestationsStatus: z
    .enum(["ALL", "AWAITING_COSIGNATURE", "SIGNED", "COSIGNED"])
    .nullish()
    .catch(null),
  attestationsOffice: z.coerce.number().int().positive().nullish().catch(null),
});

const columnHelper = createColumnHelper<AttestationListItem>();

/**
 * Every signed batch — the record of what has been attested, and the queue for the
 * second reviewer where an office requires one.
 *
 * The unit here is the batch, not the note. The nurse grouped those notes and signed
 * one declaration naming them; asking a second reviewer to re-pick notes would produce
 * a different set, and then the two signatures would cover two different lists.
 *
 * Batches that never needed a second signature appear too. They are complete the
 * moment they are signed, but they are still the signer's record of what they
 * attested to, and leaving them out meant that for an office with no second-reviewer
 * requirement a signed batch was visible nowhere in the product.
 *
 * Rendered as a tab of DailyNotesRoute. Reading is gated there on
 * `view_visits_section_daily_notes`; the co-sign action gates itself on
 * `cosign_visit_daily_note_attestation`.
 */
export default function DailyNoteAttestationsTab(props: { patientId?: PatientId }) {
  const { api, queries } = useApi();

  // Embedded in a patient's own view: the patient is fixed by that view, so there is no patient
  // filter and no heading, and no default date window -- one patient's batches are a bounded set
  // and a window would hide the oldest.
  const embeddedPatientId = props.patientId ?? null;
  const isEmbedded = embeddedPatientId !== null;

  const offices = useQuery(queries.dailyNotes.reviewOffices());

  const params = usePathParams(routeParams);
  const preset =
    isEmbedded || params.attestationsStatus === null || params.attestationsStatus === undefined
      ? null
      : {
          status: params.attestationsStatus,
          officeId: params.attestationsOffice ? OfficeId.parse(params.attestationsOffice) : null,
        };

  const patientSearch = usePatientSearchFilter();

  const [openAttestation, setOpenAttestation] = React.useState<AttestationListItem | null>(null);

  const pdf = useAttestationPdfDownload();

  const columns = React.useMemo(
    () => [
      ...(isEmbedded
        ? []
        : [
            columnHelper.accessor("patientNames", {
              header: "Patient",
              // An attestation covers one participant, so this is one name. Rows signed before
              // that rule existed could hold more, and those join rather than silently showing
              // the first of several.
              cell: (info) => <Text>{info.getValue().join(", ")}</Text>,
            }),
          ]),
      columnHelper.accessor("signedAt", {
        header: "Signed",
        cell: (info) => (
          <Text whiteSpace="nowrap">{dateFormatter.toDateTime(info.getValue())}</Text>
        ),
      }),
      columnHelper.accessor("noteCount", {
        header: "Notes",
        cell: (info) => <Text>{info.getValue()}</Text>,
      }),
      columnHelper.display({
        id: "_visitRange",
        header: "Notes date range",
        // The visit days the batch covers, so a reviewer knows what is inside before opening it.
        cell: ({ row }) => {
          const first = row.original.firstVisitDate;
          const last = row.original.lastVisitDate;

          // Falsy rather than `=== null`: a server that has not been updated yet leaves the
          // fields out altogether, and the page must still render.
          if (!first || !last) {
            return <Text color="gray.500">—</Text>;
          }

          return (
            <Text whiteSpace="nowrap">
              {first.toString() === last.toString()
                ? dateFormatter.toDate(first)
                : `${dateFormatter.toDate(first)} – ${dateFormatter.toDate(last)}`}
            </Text>
          );
        },
      }),
      columnHelper.display({
        id: "_priorities",
        header: "Concerning (AI)",
        cell: ({ row }) => (
          <Flex gap={1}>
            {row.original.urgentCount > 0 ? (
              <Tag colorScheme="red" size="sm">
                {row.original.urgentCount} urgent
              </Tag>
            ) : null}
            {row.original.highCount > 0 ? (
              <Tag colorScheme="orange" size="sm">
                {row.original.highCount} high
              </Tag>
            ) : null}
            {row.original.urgentCount === 0 && row.original.highCount === 0 ? (
              <Text color="gray.500">—</Text>
            ) : null}
          </Flex>
        ),
      }),
      // What the nurse confirmed. Whether an incident was raised for it is her separate choice,
      // so it has its own column below.
      columnHelper.accessor("nonRoutineCount", {
        header: "Confirmed concerning",
        cell: (info) =>
          info.getValue() === 0 ? (
            <Text color="gray.500">—</Text>
          ) : (
            <Text fontWeight="semibold">{info.getValue()}</Text>
          ),
      }),
      // An incident is raised only where the nurse opted in, so this counts the rows that
      // exist, not the notes ruled concerning.
      columnHelper.accessor("incidentCount", {
        // Marked in the header as well as in the modal, so the column carrying the
        // consequence is identifiable while scanning a page of rows. Unlike the tile,
        // the icon is unconditional here: it labels the column, it does not flag a
        // value.
        header: () => (
          <Flex align="center" display="inline-flex" gap={2}>
            <WarningTwoIcon color="red.500" />
            Critical incidents
          </Flex>
        ),
        cell: (info) =>
          info.getValue() === 0 ? (
            <Text color="gray.500">—</Text>
          ) : (
            <Text color="red.700" fontWeight="semibold">
              {info.getValue()}
            </Text>
          ),
      }),
      columnHelper.accessor("signedByName", {
        header: "Signed by",
        cell: (info) => <Text>{info.getValue()}</Text>,
      }),
      columnHelper.accessor("status", {
        header: "Status",
        // COMPLETE covers two different histories: counter-signed, and never needing
        // a second signature at all. Same state for a queue, different states for a
        // record — so the row says which.
        cell: ({ row }) => {
          if (row.original.status !== "COMPLETE") {
            return <Text color="purple.700">Awaiting 2nd reviewer</Text>;
          }

          return row.original.requiresCosignature ? (
            <Text color="green.700">Co-signed by {row.original.cosignedByName ?? ""}</Text>
          ) : (
            <Text color="green.700">Signed</Text>
          );
        },
      }),
      columnHelper.display({
        id: "_download",
        header: "",
        // stopPropagation: the row opens the attestation, and downloading it should
        // not also do that.
        cell: ({ row }) => (
          <Tooltip
            label={
              pdf.isPending(row.original.id)
                ? pdf.remaining(row.original.id) === null
                  ? "Preparing the PDF…"
                  : `Preparing the PDF — ${pdf.remaining(row.original.id)} notes left`
                : "Download every note in this attestation as one PDF"
            }
          >
            <IconButton
              aria-label="Download this attestation"
              icon={<DownloadIcon />}
              isDisabled={pdf.isPending(row.original.id)}
              isLoading={pdf.isPending(row.original.id)}
              size="sm"
              variant="ghost"
              onClick={(event) => {
                event.stopPropagation();
                void pdf.downloadAll(row.original.id);
              }}
            />
          </Tooltip>
        ),
      }),
    ],
    [pdf, isEmbedded]
  );

  const { table, query, totalCount, filters, setFilter, metaButtonProps } = useEndpointDataTable({
    queryKey: ["visitDailyNoteAttestations", "page", embeddedPatientId],
    columns: columns,
    columnVisiblity: {
      storage: { key: "visits/dailyNoteAttestations", version: 2 },
    },
    filters: {
      // Opens on everything: this tab is a record first and a queue second, and someone
      // arriving from the post-signing toast should see what they just signed rather
      // than an empty awaiting-list.
      //
      // Opened from a Dashboard tile, it starts on that tile's status and office instead, with
      // no date window: what waits now can have been signed long ago.
      initialState: (preset !== null
        ? {
            status: preset.status,
            officeId: preset.officeId,
            patientId: embeddedPatientId,
            fromDate: null,
            toDate: null,
          }
        : {
            status: "ALL",
            officeId: null,
            patientId: embeddedPatientId,
            // Same window the notes tab opens on, so moving between the two tabs does not
            // silently change how far back you are looking. Not for one patient's own view.
            fromDate: isEmbedded ? null : LocalDate.now().minusMonths(DEFAULT_MONTHS_BACK),
            toDate: isEmbedded ? null : LocalDate.now(),
          }) satisfies Filters as Filters,
    },
    fetchPage: async (request) => {
      const body: PageBody = {
        status: request.filters.status,
        officeId: request.filters.officeId,
        patientId: embeddedPatientId ?? request.filters.patientId,
        fromDate: request.filters.fromDate,
        toDate: request.filters.toDate,
        limit: request.limit,
        offset: request.offset,
      };

      return await api.post("./visit_daily_note_attestations/page", { body });
    },
    fetchAllForExport: async () => [],
  });

  const filterNode = (
    <>
      <Select
        label="Status"
        multiple={false}
        options={STATUS_OPTIONS}
        value={filters.status}
        onChange={(x) => setFilter("status", (x ?? "ALL") as Filters["status"])}
      />

      <Select
        allowUnselect={true}
        label="Office"
        multiple={false}
        options={(offices.data ?? []).map((office) => ({
          label: office.name,
          value: office.id,
        }))}
        value={filters.officeId}
        onChange={(x) => setFilter("officeId", x ?? null)}
      />

      {isEmbedded ? null : (
        <Select
          allowUnselect={true}
          controlledSearchTerm={[patientSearch.search, patientSearch.setSearch]}
          isLoading={patientSearch.isFetching}
          label="Patient"
          multiple={false}
          options={patientSearch.options.map((patient) => ({
            label: patient.label,
            value: patient.id,
          }))}
          searchable={true}
          value={filters.patientId}
          onChange={(x) => {
            patientSearch.remember(x);
            setFilter("patientId", x ?? null);
          }}
        />
      )}

      {/* The signing date, not the visit date — it is the date this table shows, and
          filtering a list by a column the reader cannot see is the worse surprise.
          The notes tab's equivalent filter is on the visit date. */}
      <SingleDatePicker
        placeholderText="Signed from"
        selected={filters.fromDate}
        onChange={(date) => setFilter("fromDate", date)}
      />

      <SingleDatePicker
        placeholderText="Signed to"
        selected={filters.toDate}
        onChange={(date) => setFilter("toDate", date)}
      />
    </>
  );

  return (
    <Box>
      {isEmbedded ? null : (
        <Page.Header pb={2}>
          <Flex align="center" gap={3}>
            <Page.Title>Attestations</Page.Title>
            <ResultCount count={totalCount} isLoading={query.isPending} noun="attestation" />
          </Flex>
        </Page.Header>
      )}

      <Page.Content pt={1}>
        {isEmbedded ? (
          <Box mb={2}>
            <ResultCount count={totalCount} isLoading={query.isPending} noun="attestation" />
          </Box>
        ) : null}

        <DataTable
          filterNode={filterNode}
          isLoading={query.isPending}
          metaButtonProps={metaButtonProps}
          spacing="tight"
          table={table}
          onClickRow={(_event, row) => setOpenAttestation(row.original)}
        />
      </Page.Content>

      <DailyNoteCosignModal
        attestation={openAttestation}
        onClose={() => setOpenAttestation(null)}
      />
    </Box>
  );
}
